import * as moment from 'moment';
import { utils } from '../Utils/mx-utils';
import { IQService } from 'angular';
import { HttpService } from '../App/services/http-service';
import { DataStorage } from '../App/services/mx-data-storage';
import { MxLanguage } from './language';
import { I18n } from './mx-internationalization';
import { mx } from '../App/globals';
import { ApiError } from '../Types';

const defaultLanguage = {
	Code: 'en-US',
	FlagImagePath: '/WM/Images/icons/16x16/flag_usa.png',
	Id: 'default',
	IsBaseLanguageInGroup: true,
	IsDefaultUiLanguage: true,
	IsMailLanguage: true,
	IsUiLanguage: true,
	LCID: 1033,
	ParentLCID: 9,
	ParentTitle: 'English',
	Title: 'English (United States)',
	TwoLetterCode: 'en',
	ID: 'e39c8a76-0940-e311-b2be-005056b40022',
	IsRightToLeft: false,
};

export interface LanguageDTO {
	Id: string;
	Title: string;
	Code: string;
	LCID: number;
	ParentLCID: number;
	ParentTitle: string;
	FlagImagePath: string;
	TwoLetterCode: string;
	IsRightToLeft: boolean;
	IsBaseLanguageInGroup: boolean;
	IsDefaultUiLanguage: boolean;
	IsMailLanguage: boolean;
}

type LocalizationDTO = Record<string, string>;

export class I18nService {
	public static readonly $inject = ['$q', 'mx.SolutionBuilderAgent.Http', 'mx.shell.DataStorage', 'mx.internationalization', 'tmhDynamicLocale', '$mdDateLocale'];
	public currentLanguage: MxLanguage;
	public defaultLanguage: MxLanguage;
	private _languages?: MxLanguage[];

	constructor(
		private readonly $q: IQService,
		private readonly http: HttpService,
		private readonly dataStorage: DataStorage,
		private readonly i18n: I18n,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		private readonly tmhDynamicLocale: any,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		private readonly $mdDateLocale: any
	) {
		this.currentLanguage = this.defaultLanguage = this.createLanguageItem(defaultLanguage);
	}

	async getLanguages() {
		if (this._languages) {
			return this._languages;
		}

		let data: LanguageDTO[] = [];

		try {
			data = await this.http.get<LanguageDTO[]>('api/internationalization/languages');
		} catch (e) {
			mx.logCatch(e as ApiError);
		} finally {
			if (!data.length) {
				data = [defaultLanguage];
			}
		}

		this._languages = data.map(this.createLanguageItem, this);

		return this._languages;
	}

	createLanguageItem(item: LanguageDTO) {
		return new MxLanguage({
			id: item.Id,
			title: item.Title,
			code: item.Code,
			lcid: item.LCID,
			parentLcid: item.ParentLCID,
			parentTitle: item.ParentTitle,
			flagUrl: item.FlagImagePath,
			twoLetterCode: item.TwoLetterCode,
			isRightToLeft: item.IsRightToLeft,
			isBaseLanguageInGroup: item.IsBaseLanguageInGroup,
			isDefaultUiLanguage: item.IsDefaultUiLanguage,
			isMailLanguage: item.IsMailLanguage,
		});
	}

	async getLanguageByCode(languageCode: string) {
		const languages = await this.getLanguages();
		return languages.find((language) => language.code === languageCode || language.twoLetterCode === languageCode);
	}

	async getLanguageById(languageId: string) {
		const languages = await this.getLanguages();
		return languages.find((language) => language.id === languageId);
	}

	async getDefaultUiLanguage() {
		const languages = await this.getLanguages();
		return languages.find((lng) => lng.isDefaultUiLanguage) || languages[0];
	}

	async initLocalization(languageId: string) {
		const { i18n, http } = this;

		if (languageId === this.currentLanguage.id && i18n.localization) {
			return;
		}

		const language = (await this.getLanguageById(languageId)) || (await this.getDefaultUiLanguage());
		this._setLanguage(language);

		try {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			i18n.localization = await http.get<LocalizationDTO>('api/localization/frontend');
		} catch (e) {
			console.error('Localization loading fail:', e);
		}
	}

	async getSupportedBrowserLanguage() {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const IElanguage = navigator.userLanguage;
		const explicitLanguage = utils.getCookie('explicitLanguage') || navigator.language || IElanguage;
		const language = await this.getLanguageByCode(explicitLanguage);
		return language || this.defaultLanguage;
	}

	_setLanguage(lng: MxLanguage) {
		this.currentLanguage = lng;

		const { i18n, tmhDynamicLocale, $mdDateLocale } = this;

		const date = new Date().setFullYear(new Date().getFullYear() + 1);
		document.cookie = 'explicitLanguage=' + (lng.code || this.defaultLanguage.code) + '; path=/; expires=' + new Date(date).toUTCString();

		const momentLocale = utils.getMomentJsSupportedLocale(lng.code);
		moment.locale(momentLocale);

		$mdDateLocale.shortDays = moment.localeData().weekdaysMin();
		i18n.language = moment.locales().indexOf(lng.code.toLowerCase()) > -1 ? lng.code.toLowerCase() : lng.twoLetterCode;
		tmhDynamicLocale.set(lng.code.toLowerCase());

		document.body.setAttribute('dir', lng.isRightToLeft ? 'rtl' : '');
		$('html').attr('lang', lng.code);
	}
}
